import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUpload, FaFileExcel } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { Tabs, Select, Pagination } from 'antd';
import ApplicationService from '../services/axios.service';
import Spinner from '../components/spinner';
import * as ExcelJS from 'exceljs';

const { TabPane } = Tabs;
const service = new ApplicationService();

const SalesTotal = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  
  const [data, setData] = useState({});
  const [budgetData, setBudgetData] = useState({});
  const [loading, setLoading] = useState(false);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarters, setSelectedQuarters] = useState(['Q1']);
  const [budgetYear, setBudgetYear] = useState(currentYear);
  const [budgetQuarters, setBudgetQuarters] = useState(['Q1']);
  const [availableForecastYears, setAvailableForecastYears] = useState([]);
  const [availableBudgetYears, setAvailableBudgetYears] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [budgetCustomers, setBudgetCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedBudgetCustomer, setSelectedBudgetCustomer] = useState(null);

  
  // Pagination states for Forecast
  const [forecastCurrentPage, setForecastCurrentPage] = useState(1);
  const [forecastRecordsPerPage, setForecastRecordsPerPage] = useState(100);
  const [forecastTotal, setForecastTotal] = useState(0);

  // Pagination states for Budget
  const [budgetCurrentPage, setBudgetCurrentPage] = useState(1);
  const [budgetRecordsPerPage, setBudgetRecordsPerPage] = useState(100);
  const [budgetTotal, setBudgetTotal] = useState(0);
  
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [budgetEditingCell, setBudgetEditingCell] = useState(null);
  const [budgetEditValue, setBudgetEditValue] = useState('');
  const [budgetHasUnsavedChanges, setBudgetHasUnsavedChanges] = useState(false);
  const [budgetPendingChanges, setBudgetPendingChanges] = useState({});
  const editValueRef = useRef('');
  const editingCellRef = useRef(null);

  // Update refs when state changes
  useEffect(() => {
    editValueRef.current = editValue;
  }, [editValue]);

  useEffect(() => {
    editingCellRef.current = editingCell;
  }, [editingCell]);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await service.getSalesTotalCustomers();
      if (response.data.statusCode === 200) {
        const customerList = response.data.data.forecast || [];
        setCustomers(customerList);
        if (customerList.length > 0) {
          setSelectedCustomer(customerList[0].customer_id);
        }
          const budgetCustomerList = response.data.data.budget || [];
          setBudgetCustomers(budgetCustomerList);
          if (budgetCustomerList.length > 0) {
            setSelectedBudgetCustomer(budgetCustomerList[0].customer_id);
          }
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    }
  }, []);

  const fetchSalesTotalData = useCallback(async () => {
    if (selectedQuarters.length === 0 || !selectedCustomer) return;

    setLoading(true);
    try {
      const payload = {
        year: selectedYear,
        quarters: selectedQuarters.join(','),
        customer_id: selectedCustomer,
        page: forecastCurrentPage,
        limit: forecastRecordsPerPage
      };

      console.log('📤 Forecast API Payload:', payload); // Debug log

      const response = await service.getSalesTotalGrouped(payload);
      
      console.log('📥 Forecast API Response:', response.data); // Debug log
      
      if (response.data.success) {
        setData({ 
          records: response.data.data.records || [], 
          months: response.data.data.months || [] 
        });
        setForecastTotal(response.data.data.pagination?.total || 0);
        
        console.log('✅ Forecast Total:', response.data.data.pagination?.total);
        console.log('✅ Records Count:', response.data.data.records?.length);
      } else {
        setData({ records: [], months: [] });
        setForecastTotal(0);
      }
    } catch (error) {
      console.error('❌ Error fetching sales total:', error);
      setData({ records: [], months: [] });
      setForecastTotal(0);
    } finally {
      setLoading(false);
    }
  }, [selectedQuarters, selectedYear, selectedCustomer, forecastCurrentPage, forecastRecordsPerPage]);

  const fetchBudgetData = useCallback(async () => {
    if (budgetQuarters.length === 0 || !selectedBudgetCustomer) return;

    setBudgetLoading(true);
    try {
      const payload = {
        year: budgetYear,
        quarters: budgetQuarters.join(','),
        customer_id: selectedBudgetCustomer,
        page: budgetCurrentPage,
        limit: budgetRecordsPerPage
      };

      console.log('Budget payload:', payload); // Debug log

      const response = await service.getSalesBudgetGrouped(payload);
      
      if (response.data.success) {
        setBudgetData({ 
          data: response.data.data.records || [], 
          months: response.data.data.months || [] 
        });
        setBudgetTotal(response.data.data.pagination?.total || 0);
      } else {
        setBudgetData({ data: [], months: [] });
        setBudgetTotal(0);
      }
    } catch (error) {
      console.error('Error fetching budget data:', error);
      setBudgetData({ data: [], months: [] });
      setBudgetTotal(0);
    } finally {
      setBudgetLoading(false);
    }
  }, [budgetQuarters, budgetYear, selectedBudgetCustomer, budgetCurrentPage, budgetRecordsPerPage]);

  const fetchForecastYears = useCallback(async () => {
    try {
      const response = await service.getSalesTotalYears();
      if (response.data.statusCode === 200) {
        const years = response.data.data || [];
        setAvailableForecastYears(years);
        if (years.length > 0) {
          setSelectedYear(Number(years[0]));
        }
      }
    } catch (error) {
      console.error('Error fetching forecast years:', error);
      setAvailableForecastYears([currentYear]);
    }
  }, [currentYear]);

  const fetchBudgetYears = useCallback(async () => {
    try {
      const response = await service.getSalesBudgetYears();
      if (response.data.statusCode === 200) {
        const years = response.data.data || [];
        setAvailableBudgetYears(years);
        if (years.length > 0) {
          setBudgetYear(Number(years[0]));
        }
      }
    } catch (error) {
      console.error('Error fetching budget years:', error);
      setAvailableBudgetYears([currentYear]);
    }
  }, [currentYear]);

  useEffect(() => {
    fetchCustomers();
    fetchForecastYears();
    fetchBudgetYears();
  }, [fetchCustomers, fetchForecastYears, fetchBudgetYears]);

  useEffect(() => {
    if (availableForecastYears.length > 0 && selectedCustomer) {
      fetchSalesTotalData();
    }
  }, [selectedQuarters, selectedYear, selectedCustomer, forecastCurrentPage, forecastRecordsPerPage, availableForecastYears.length, fetchSalesTotalData]);

  useEffect(() => {
    if (availableBudgetYears.length > 0 && selectedBudgetCustomer) {
      fetchBudgetData();
    }
  }, [budgetQuarters, budgetYear, selectedBudgetCustomer, budgetCurrentPage, budgetRecordsPerPage, availableBudgetYears.length, fetchBudgetData]);

  const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];

  // Get current month and year
  const getCurrentMonthYear = () => {
    const now = new Date();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[now.getMonth()]} ${now.getFullYear()}`;
  };

  const isCurrentMonth = (monthKey) => {
    const currentMonthYear = getCurrentMonthYear();
    return monthKey === currentMonthYear;
  };

  const handleQuarterChange = (quarter) => {
    setSelectedQuarters(prev => 
      prev.includes(quarter) 
        ? prev.filter(q => q !== quarter)
        : [...prev, quarter]
    );
  };

  const handleCellEdit = (customerName, partNumber, month, field, value) => {
    setData(prevData => {
      const newData = { ...prevData };
      const monthRecords = newData[month];
      const recordIndex = monthRecords.findIndex(
        r => r.customer_name === customerName && r.part_number === partNumber
      );
      if (recordIndex > -1) {
        monthRecords[recordIndex] = {
          ...monthRecords[recordIndex],
          [field]: value
        };
      }
      return newData;
    });
  };

  const startEdit = (customerName, partNumber, month, field, currentValue) => {
    const cellData = { customerName, partNumber, month, field };
    
    // Handle both string and number values from API
    let valueToEdit = '';
    if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
      // Convert to number first, then to string for editing
      const numVal = typeof currentValue === 'string' ? parseFloat(currentValue) : currentValue;
      valueToEdit = isNaN(numVal) ? '' : String(numVal);
    }
    
    setEditingCell(cellData);
    setEditValue(valueToEdit);
    editingCellRef.current = cellData;
    editValueRef.current = valueToEdit;
  };

  const handleEditChange = (e) => {
    const newValue = e.target.value;
    setEditValue(newValue);
    
    const cell = editingCell;
    if (!cell) return;

    const numericValue = newValue === '' || newValue === null ? 0 : parseFloat(newValue);
    
    if (isNaN(numericValue)) {
      return;
    }

    const key = `${cell.customerName}_${cell.partNumber}_${cell.month}_${cell.field}`;
    
    setPendingChanges(prev => ({
      ...prev,
      [key]: {
        customerName: cell.customerName,
        partNumber: cell.partNumber,
        month: cell.month,
        field: cell.field,
        value: numericValue
      }
    }));

    // Update local display immediately
    setData(prevData => {
      const newRecords = prevData.records.map(record => {
        if (record.customer_name === cell.customerName && 
            record.part_number === cell.partNumber) {
          const updatedMonths = { ...record.months };
          if (updatedMonths[cell.month]) {
            updatedMonths[cell.month] = {
              ...updatedMonths[cell.month],
              [cell.field]: numericValue
            };
          }
          return { ...record, months: updatedMonths };
        }
        return record;
      });
      return { ...prevData, records: newRecords };
    });

    setHasUnsavedChanges(true);
  };

  const saveEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      saveEdit();
    }
  };

  const handleSaveAll = async () => {
    if (Object.keys(pendingChanges).length === 0) { 
      toast.info("No changes to save"); 
      return; 
    }
    try {
      const updates = Object.values(pendingChanges).map(change => ({
        customer_name: change.customerName, 
        part_number: change.partNumber, 
        month: change.month, 
        [change.field]: change.value
      }));
      for (const payload of updates) { 
        await service.updateSalesTotal(payload); 
      }
      toast.success("All changes saved successfully!");
      setHasUnsavedChanges(false); 
      setPendingChanges({});
      fetchSalesTotalData();
    } catch (error) { 
      toast.error("Error saving changes"); 
    }
  };

  const handleCancelAll = () => { 
    setPendingChanges({}); 
    setHasUnsavedChanges(false); 
    fetchSalesTotalData(); 
  };

  const isEditing = (customerName, partNumber, month, field) => {
    return editingCell?.customerName === customerName &&
           editingCell?.partNumber === partNumber &&
           editingCell?.month === month &&
           editingCell?.field === field;
  };

  // Extract records and periods from data
  const allRecords = useMemo(() => {
    return data?.records || [];
  }, [data]);

  const allPeriods = useMemo(() => {
    return data?.months || [];
  }, [data]);

const renderDataTable = (forecastData) => {
  console.log('Rendering table with data:', forecastData);

  if (!forecastData || !forecastData.records || forecastData.records.length === 0) {
    return <div className="text-center py-8 text-gray-500">No data available</div>;
  }

  const records = forecastData.records || [];
  const periods = forecastData.months || [];

  // Group records by customer_name, part_number, AND criteria
  const groupedRecords = {};
  records.forEach((record) => {
    const key = `${record.customer_name}_${record.part_number}_${record.criteria || 'NULL'}`;
    if (!groupedRecords[key]) {
      groupedRecords[key] = {
        customer_name: record.customer_name,
        part_number: record.part_number,
        model_name: record.model_name,
        sbu: record.sbu,
        business: record.business,
        market: record.market,
        is_customer: record.is_customer,
        criteria: record.criteria,
        person_in_charge: record.person_in_charge,
        new_current: record.new_current,
        months: record.months || {}
      };
    }
  });

  // Extract dynamic headers from first record
  let dynamicHeaders = [];
  const firstRecord = Object.values(groupedRecords)[0];
  if (firstRecord && firstRecord.months) {
    const firstMonth = Object.keys(firstRecord.months)[0];
    if (firstMonth && firstRecord.months[firstMonth]) {
      dynamicHeaders = Object.keys(firstRecord.months[firstMonth]).filter(
        key => key !== 'unit_price' && key !== 'qty' && key !== 'amt' && key !== 'delta'
      );
    }
  }

  // Date utilities
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonthNum = currentDate.getMonth() + 1;
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const getQuarterForPeriod = (period) => {
    if (!period) return null;
    const [monthName] = period.split(' ');
    const monthIndex = monthNames.indexOf(monthName);
    if (monthIndex === -1) return null;
    return Math.floor(monthIndex / 3) + 1;
  };

  const formatHeaderLabel = (header) => {
    return header.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const calculateQuarterTotal = (group, field) => {
    let total = 0;
    Object.values(group.months).forEach(monthData => {
      total += parseFloat(monthData[field] || 0);
    });
    return total;
  };

  const shouldShowAllColumns = (period) => {
    if (/^\d{4}$/.test(period)) {
      const periodYear = parseInt(period);
      return periodYear > currentYear;
    }
    
    const [monthName, yearStr] = period.split(' ');
    const monthIndex = monthNames.indexOf(monthName);
    if (monthIndex === -1) return false;
    
    const periodYear = parseInt(yearStr);
    const periodMonth = monthIndex + 1;
    
    return periodYear > currentYear || (periodYear === currentYear && periodMonth >= currentMonthNum);
  };

  const isPastMonth = (period) => {
    if (/^\d{4}$/.test(period)) {
      return parseInt(period) < currentYear;
    }
    
    const [monthName, yearStr] = period.split(' ');
    const monthIndex = monthNames.indexOf(monthName);
    if (monthIndex === -1) return false;
    
    const periodYear = parseInt(yearStr);
    const periodMonth = monthIndex + 1;
    
    return periodYear < currentYear || (periodYear === currentYear && periodMonth < currentMonthNum);
  };

  const renderEditableCell = (customerName, partNumber, month, field, value, bgColor = '') => {
    const editing = isEditing(customerName, partNumber, month, field);

    return (
      <td className={`border border-gray-300 px-2 py-1 text-right ${bgColor}`} style={{ fontSize: '11px', minWidth: '80px' }}>
        {editing ? (
          <input
            type="number"
            value={editValue}
            onChange={handleEditChange}
            onBlur={saveEdit}
            onKeyDown={handleKeyDown}
            autoFocus
            className="w-full px-1 border border-blue-500 focus:outline-none"
            style={{ fontSize: '11px' }}
          />
        ) : (
          <span
            onDoubleClick={() => startEdit(customerName, partNumber, month, field, value)}
            className="cursor-pointer hover:bg-blue-50 block w-full h-full px-1"
            title="Double-click to edit"
          >
            {value || 0}
          </span>
        )}
      </td>
    );
  };

  const getStickyStyle = (index, isHeader = false) => {
    const leftPositions = [0, 100, 180, 280, 340, 400, 460, 520];
    const widths = [100, 80, 100, 60, 60, 60, 60, 60];
    
    return {
      position: 'sticky',
      left: `${leftPositions[index]}px`,
      backgroundColor: isHeader ? '#fef08a' : 'white',
      zIndex: isHeader ? 30 : 10,
      minWidth: `${widths[index]}px`,
      maxWidth: `${widths[index]}px`,
      fontSize: '11px',
      whiteSpace: 'normal',
      wordWrap: 'break-word',
      overflow: 'visible'
    };
  };

  return (
    <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
      <table className="min-w-full border-collapse border border-gray-300" style={{ fontSize: '11px' }}>
        <thead className="sticky top-0 z-20">
          <tr className="bg-yellow-200">
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(0, true)}>Customer Name</th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(1, true)}>Part Number</th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(2, true)}>Model Name</th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(3, true)}>SBU</th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(4, true)}>Business</th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(5, true)}>Market</th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(6, true)}>Customer</th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(7, true)}>Person In Charge</th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(8, true)}>New/Current</th>


            {/* <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(9, true)}>Criteria</th> */}
            {periods.map((period, idx) => {
              const showAllColumns = shouldShowAllColumns(period);
              const currentQuarter = getQuarterForPeriod(period);
              const nextPeriod = periods[idx + 1];
              const nextQuarter = nextPeriod ? getQuarterForPeriod(nextPeriod) : null;
              const isLastInQuarter = currentQuarter !== nextQuarter;
              
              const baseColumns = 3;
              const extraColumns = showAllColumns ? dynamicHeaders.length : 0;
              const quarterColumns = isLastInQuarter ? 3 : 0;
              const colSpan = baseColumns + extraColumns + quarterColumns;

              return (
                <th key={idx} className="border border-gray-300 px-2 py-1 text-center bg-yellow-300" colSpan={colSpan}>
                  {period}
                </th>
              );
            })}
          </tr>

          <tr className="bg-blue-100">
            {periods.map((period, periodIdx) => {
              const showAllColumns = shouldShowAllColumns(period);
              const currentQuarter = getQuarterForPeriod(period);
              const nextPeriod = periods[periodIdx + 1];
              const nextQuarter = nextPeriod ? getQuarterForPeriod(nextPeriod) : null;
              const isLastInQuarter = currentQuarter !== nextQuarter;

              return (
                <React.Fragment key={periodIdx}>
                  <th className="border border-gray-300 px-2 py-1 text-center" style={{ minWidth: '80px' }}>Unit Price</th>
                  <th className="border border-gray-300 px-2 py-1 text-center" style={{ minWidth: '80px' }}>Qty</th>
                  <th className="border border-gray-300 px-2 py-1 text-center" style={{ minWidth: '80px' }}>Amt</th>
                  
                  {showAllColumns && dynamicHeaders.map((header, hIdx) => (
                    <th key={hIdx} className="border border-gray-300 px-2 py-1 text-center bg-blue-50" style={{ minWidth: '100px' }}>
                      {formatHeaderLabel(header)}
                    </th>
                  ))}

                  {isLastInQuarter && (
                    <>
                      <th className="border border-gray-300 px-2 py-1 text-center bg-purple-100" style={{ minWidth: '80px' }}>Q Total Qty</th>
                      <th className="border border-gray-300 px-2 py-1 text-center bg-purple-100" style={{ minWidth: '80px' }}>Q Total Amt</th>
                      <th className="border border-gray-300 px-2 py-1 text-center bg-orange-100" style={{ minWidth: '80px' }}>Delta</th>
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {Object.values(groupedRecords).map((group, rowIdx) => (
            <tr key={rowIdx} className="hover:bg-gray-50">
              <td className="border border-gray-300 px-2 py-1" style={getStickyStyle(0)}>{group.customer_name}</td>
              <td className="border border-gray-300 px-2 py-1" style={getStickyStyle(1)}>{group.part_number}</td>
              <td className="border border-gray-300 px-2 py-1" style={getStickyStyle(2)}>{group.model_name}</td>
              <td className="border text-center border-gray-300 px-2 py-1" style={getStickyStyle(3)}>{group.sbu}</td>
              <td className="border text-center border-gray-300 px-2 py-1" style={getStickyStyle(4)}>{group.business}</td>
              <td className="border text-center border-gray-300 px-2 py-1" style={getStickyStyle(5)}>{group.market}</td>
              <td className="border text-center border-gray-300 px-2 py-1" style={getStickyStyle(6)}>{group.is_customer}</td>
                            <td className="border text-center border-gray-300 px-2 py-1" style={getStickyStyle(7)}>{group.person_in_charge}</td>
              <td className="border text-center border-gray-300 px-2 py-1" style={getStickyStyle(8)}>{group.new_current}</td>
              {/* <td className="border text-center border-gray-300 px-2 py-1" style={getStickyStyle(9)}>{group.criteria}</td> */}
              
              {periods.map((period, periodIdx) => {
                const monthData = group.months[period] || {};
                const showAllColumns = shouldShowAllColumns(period);
                const isPast = isPastMonth(period);
                const currentQuarter = getQuarterForPeriod(period);
                const nextPeriod = periods[periodIdx + 1];
                const nextQuarter = nextPeriod ? getQuarterForPeriod(nextPeriod) : null;
                const isLastInQuarter = currentQuarter !== nextQuarter;

                return (
                  <React.Fragment key={periodIdx}>
                    {isPast ? (
                      <>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-gray-100" style={{ fontSize: '11px' }}>{monthData.unit_price || 0}</td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-gray-100" style={{ fontSize: '11px' }}>{monthData.qty || 0}</td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-gray-100" style={{ fontSize: '11px' }}>{monthData.amt || 0}</td>
                      </>
                    ) : (
                      <>
                        {renderEditableCell(group.customer_name, group.part_number, period, 'unit_price', monthData.unit_price)}
                        {renderEditableCell(group.customer_name, group.part_number, period, 'qty', monthData.qty)}
                        {renderEditableCell(group.customer_name, group.part_number, period, 'amt', monthData.amt)}
                      </>
                    )}
                    
                    {showAllColumns && !isPast && dynamicHeaders.map((header, hIdx) => (
                      <React.Fragment key={hIdx}>
                        {renderEditableCell(group.customer_name, group.part_number, period, header, monthData[header], 'bg-blue-50')}
                      </React.Fragment>
                    ))}

                    {showAllColumns && isPast && dynamicHeaders.map((header, hIdx) => (
                      <td key={hIdx} className="border border-gray-300 px-2 py-1 text-right bg-gray-100" style={{ fontSize: '11px' }}>
                        {monthData[header] || 0}
                      </td>
                    ))}

                    {isLastInQuarter && (
                      <>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-purple-50" style={{ fontSize: '11px' }}>
                          {calculateQuarterTotal(group, 'qty').toFixed(2)}
                        </td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-purple-50" style={{ fontSize: '11px' }}>
                          {calculateQuarterTotal(group, 'amt').toFixed(2)}
                        </td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-orange-50" style={{ fontSize: '11px' }}>
                          {parseFloat(monthData.delta || 0).toFixed(2)}
                        </td>
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
  const renderBudgetDataTable = (budgetData) => {
  if (!budgetData || !budgetData.data || budgetData.data.length === 0) {
    return <div className="text-center py-8 text-gray-500">No budget data available</div>;
  }

  const records = budgetData.data || [];
  const periods = budgetData.months || [];

  // Group records by customer_name, part_number, AND criteria
  const groupedRecords = {};
  records.forEach((record) => {
    const key = `${record.customer_name}_${record.part_number}_${record.criteria || 'NULL'}`;
    if (!groupedRecords[key]) {
      groupedRecords[key] = {
        customer_name: record.customer_name,
        part_number: record.part_number,
        model_name: record.model_name,
        sbu: record.sbu,
        business: record.business,
        market: record.market,
        is_customer: record.is_customer,
        criteria: record.criteria,
        months: record.months || {}
      };
    }
  });

  // Extract dynamic headers from first record
  let dynamicHeaders = [];
  const firstRecord = Object.values(groupedRecords)[0];
  if (firstRecord && firstRecord.months) {
    const firstMonth = Object.keys(firstRecord.months)[0];
    if (firstMonth && firstRecord.months[firstMonth]) {
      dynamicHeaders = Object.keys(firstRecord.months[firstMonth]).filter(
        key => key !== 'unit_price' && key !== 'qty' && key !== 'amt' && key !== 'delta'
      );
    }
  }

  // Date utilities
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonthNum = currentDate.getMonth() + 1;
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const getQuarterForPeriod = (period) => {
    if (!period) return null;
    const [monthName] = period.split(' ');
    const monthIndex = monthNames.indexOf(monthName);
    if (monthIndex === -1) return null;
    return Math.floor(monthIndex / 3) + 1;
  };

  const formatHeaderLabel = (header) => {
    return header.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const calculateQuarterTotal = (group, field) => {
    let total = 0;
    Object.values(group.months).forEach(monthData => {
      total += parseFloat(monthData[field] || 0);
    });
    return total;
  };

  const shouldShowAllColumns = (period) => {
    if (/^\d{4}$/.test(period)) {
      const periodYear = parseInt(period);
      return periodYear > currentYear;
    }
    
    const [monthName, yearStr] = period.split(' ');
    const monthIndex = monthNames.indexOf(monthName);
    if (monthIndex === -1) return false;
    
    const periodYear = parseInt(yearStr);
    const periodMonth = monthIndex + 1;
    
    return periodYear > currentYear || (periodYear === currentYear && periodMonth >= currentMonthNum);
  };

  const isPastMonth = (period) => {
    if (/^\d{4}$/.test(period)) {
      return parseInt(period) < currentYear;
    }
    
    const [monthName, yearStr] = period.split(' ');
    const monthIndex = monthNames.indexOf(monthName);
    if (monthIndex === -1) return false;
    
    const periodYear = parseInt(yearStr);
    const periodMonth = monthIndex + 1;
    
    return periodYear < currentYear || (periodYear === currentYear && periodMonth < currentMonthNum);
  };

  const renderEditableCell = (customerName, partNumber, month, field, value, bgColor = '') => {
    const editing = isEditing(customerName, partNumber, month, field);

    return (
      <td className={`border border-gray-300 px-2 py-1 text-right ${bgColor}`} style={{ fontSize: '11px', minWidth: '80px' }}>
        {editing ? (
          <input
            type="number"
            value={editValue}
            onChange={handleEditChange}
            onBlur={saveEdit}
            onKeyDown={handleKeyDown}
            autoFocus
            className="w-full px-1 border border-blue-500 focus:outline-none"
            style={{ fontSize: '11px' }}
          />
        ) : (
          <span
            onDoubleClick={() => startEdit(customerName, partNumber, month, field, value)}
            className="cursor-pointer hover:bg-blue-50 block w-full h-full px-1"
            title="Double-click to edit"
          >
            {value || 0}
          </span>
        )}
      </td>
    );
  };

  const getStickyStyle = (index, isHeader = false) => {
    const leftPositions = [0, 100, 180, 280, 340, 400, 460, 520];
    const widths = [100, 80, 100, 60, 60, 60, 60, 60];
    
    return {
      position: 'sticky',
      left: `${leftPositions[index]}px`,
      backgroundColor: isHeader ? '#fef08a' : 'white',
      zIndex: isHeader ? 30 : 10,
      minWidth: `${widths[index]}px`,
      maxWidth: `${widths[index]}px`,
      fontSize: '11px',
      whiteSpace: 'normal',
      wordWrap: 'break-word',
      overflow: 'visible'
    };
  };

  return (
    <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
      <table className="min-w-full border-collapse border border-gray-300" style={{ fontSize: '11px' }}>
        <thead className="sticky top-0 z-20">
          <tr className="bg-yellow-200">
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(0, true)}>Customer Name</th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(1, true)}>Part Number</th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(2, true)}>Model Name</th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(3, true)}>SBU</th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(4, true)}>Business</th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(5, true)}>Market</th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(6, true)}>Customer</th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(7, true)}>Person In Charge</th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(8, true)}>New/Current</th>


            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(9, true)}>Criteria</th>
            {periods.map((period, idx) => {
              const showAllColumns = shouldShowAllColumns(period);
              const currentQuarter = getQuarterForPeriod(period);
              const nextPeriod = periods[idx + 1];
              const nextQuarter = nextPeriod ? getQuarterForPeriod(nextPeriod) : null;
              const isLastInQuarter = currentQuarter !== nextQuarter;
              
              const baseColumns = 3;
              const extraColumns = showAllColumns ? dynamicHeaders.length : 0;
              const quarterColumns = isLastInQuarter ? 3 : 0;
              const colSpan = baseColumns + extraColumns + quarterColumns;

              return (
                <th key={idx} className="border border-gray-300 px-2 py-1 text-center bg-yellow-300" colSpan={colSpan}>
                  {period}
                </th>
              );
            })}
          </tr>

          <tr className="bg-blue-100">
            {periods.map((period, periodIdx) => {
              const showAllColumns = shouldShowAllColumns(period);
              const currentQuarter = getQuarterForPeriod(period);
              const nextPeriod = periods[periodIdx + 1];
              const nextQuarter = nextPeriod ? getQuarterForPeriod(nextPeriod) : null;
              const isLastInQuarter = currentQuarter !== nextQuarter;

              return (
                <React.Fragment key={periodIdx}>
                  <th className="border border-gray-300 px-2 py-1 text-center" style={{ minWidth: '80px' }}>Unit Price</th>
                  <th className="border border-gray-300 px-2 py-1 text-center" style={{ minWidth: '80px' }}>Qty</th>
                  <th className="border border-gray-300 px-2 py-1 text-center" style={{ minWidth: '80px' }}>Amt</th>
                  
                  {showAllColumns && dynamicHeaders.map((header, hIdx) => (
                    <th key={hIdx} className="border border-gray-300 px-2 py-1 text-center bg-blue-50" style={{ minWidth: '100px' }}>
                      {formatHeaderLabel(header)}
                    </th>
                  ))}

                  {isLastInQuarter && (
                    <>
                      <th className="border border-gray-300 px-2 py-1 text-center bg-purple-100" style={{ minWidth: '80px' }}>Q Total Qty</th>
                      <th className="border border-gray-300 px-2 py-1 text-center bg-purple-100" style={{ minWidth: '80px' }}>Q Total Amt</th>
                      <th className="border border-gray-300 px-2 py-1 text-center bg-orange-100" style={{ minWidth: '80px' }}>Delta</th>
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {Object.values(groupedRecords).map((group, rowIdx) => (
            <tr key={rowIdx} className="hover:bg-gray-50">
              <td className="border border-gray-300 px-2 py-1" style={getStickyStyle(0)}>{group.customer_name}</td>
              <td className="border border-gray-300 px-2 py-1" style={getStickyStyle(1)}>{group.part_number}</td>
              <td className="border border-gray-300 px-2 py-1" style={getStickyStyle(2)}>{group.model_name}</td>
              <td className="border text-center border-gray-300 px-2 py-1" style={getStickyStyle(3)}>{group.sbu}</td>
              <td className="border text-center border-gray-300 px-2 py-1" style={getStickyStyle(4)}>{group.business}</td>
              <td className="border text-center border-gray-300 px-2 py-1" style={getStickyStyle(5)}>{group.market}</td>
              <td className="border text-center border-gray-300 px-2 py-1" style={getStickyStyle(6)}>{group.is_customer}</td>
                            <td className="border text-center border-gray-300 px-2 py-1" style={getStickyStyle(7)}>{group.person_in_charge}</td>
              <td className="border text-center border-gray-300 px-2 py-1" style={getStickyStyle(8)}>{group.new_current}</td>
              <td className="border text-center border-gray-300 px-2 py-1" style={getStickyStyle(9)}>{group.criteria}</td>
              
              {periods.map((period, periodIdx) => {
                const monthData = group.months[period] || {};
                const showAllColumns = shouldShowAllColumns(period);
                const isPast = isPastMonth(period);
                const currentQuarter = getQuarterForPeriod(period);
                const nextPeriod = periods[periodIdx + 1];
                const nextQuarter = nextPeriod ? getQuarterForPeriod(nextPeriod) : null;
                const isLastInQuarter = currentQuarter !== nextQuarter;

                return (
                  <React.Fragment key={periodIdx}>
                    {isPast ? (
                      <>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-gray-100" style={{ fontSize: '11px' }}>{monthData.unit_price || 0}</td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-gray-100" style={{ fontSize: '11px' }}>{monthData.qty || 0}</td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-gray-100" style={{ fontSize: '11px' }}>{monthData.amt || 0}</td>
                      </>
                    ) : (
                      <>
                        {renderEditableCell(group.customer_name, group.part_number, period, 'unit_price', monthData.unit_price)}
                        {renderEditableCell(group.customer_name, group.part_number, period, 'qty', monthData.qty)}
                        {renderEditableCell(group.customer_name, group.part_number, period, 'amt', monthData.amt)}
                      </>
                    )}
                    
                    {showAllColumns && !isPast && dynamicHeaders.map((header, hIdx) => (
                      <React.Fragment key={hIdx}>
                        {renderEditableCell(group.customer_name, group.part_number, period, header, monthData[header], 'bg-blue-50')}
                      </React.Fragment>
                    ))}

                    {showAllColumns && isPast && dynamicHeaders.map((header, hIdx) => (
                      <td key={hIdx} className="border border-gray-300 px-2 py-1 text-right bg-gray-100" style={{ fontSize: '11px' }}>
                        {monthData[header] || 0}
                      </td>
                    ))}

                    {isLastInQuarter && (
                      <>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-purple-50" style={{ fontSize: '11px' }}>
                          {calculateQuarterTotal(group, 'qty').toFixed(2)}
                        </td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-purple-50" style={{ fontSize: '11px' }}>
                          {calculateQuarterTotal(group, 'amt').toFixed(2)}
                        </td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-orange-50" style={{ fontSize: '11px' }}>
                          {parseFloat(monthData.delta || 0).toFixed(2)}
                        </td>
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
const renderBudgetTable = (budgetDataObj) => {
  if (!budgetDataObj || !budgetDataObj.data || budgetDataObj.data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No budget data available.
      </div>
    );
  }

  const records = budgetDataObj.data;
  const monthHeaders = budgetDataObj.months || [];

  const groupedRecords = {};
  
  records.forEach((record) => {
    const key = `${record.customer_name}_${record.part_number}`;
    if (!groupedRecords[key]) {
      groupedRecords[key] = {
        customer_name: record.customer_name,
        part_number: record.part_number,
        model_name: record.model_name,
        sbu: record.sbu,
        business: record.business,
        market: record.market,
        is_customer: record.is_customer,
          criteria: record.criteria,
        months: record.months || {}
      };
    }
  });

  let childHeaders = [];
  const firstRecord = Object.values(groupedRecords)[0];
  if (firstRecord && firstRecord.months && monthHeaders.length > 0) {
    const firstMonth = monthHeaders[0];
    if (firstMonth && firstRecord.months[firstMonth]) {
      childHeaders = Object.keys(firstRecord.months[firstMonth]).filter(
        key => key !== 'unit_price' && key !== 'qty' && key !== 'amt'
      );
    }
  }

  const renderEditableCell = (customerName, partNumber, month, field, value, bgColor = '') => {
    const isEditing = budgetEditingCell?.customerName === customerName &&
                      budgetEditingCell?.partNumber === partNumber &&
                      budgetEditingCell?.month === month &&
                      budgetEditingCell?.field === field;

    return (
      <td
        className={`border border-gray-300 px-2 py-1 text-right cursor-pointer hover:bg-blue-100 ${bgColor}`}
        style={{ fontSize: '13px', minWidth: '100px' }}
        onDoubleClick={() => startBudgetEdit(customerName, partNumber, month, field, value)}
      >
        {isEditing ? (
          <input
            type="number"
            value={budgetEditValue}
            onChange={(e) => setBudgetEditValue(e.target.value)}
            onBlur={saveBudgetEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                saveBudgetEdit();
              } else if (e.key === 'Escape') {
                setBudgetEditingCell(null);
                setBudgetEditValue('');
              }
            }}
            autoFocus
            className="w-full px-1 py-0 border border-blue-500 rounded text-right"
            style={{ fontSize: '13px' }}
          />
        ) : (
          parseFloat(value || 0).toFixed(2)
        )}
      </td>
    );
  };

  return (
    <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
      <table className="min-w-full border-collapse border border-gray-300" style={{ fontSize: '12px' }}>
        <thead className="bg-gray-100 sticky top-0 z-20">
          <tr>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(0, true)}>
              Customer Name
            </th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(1, true)}>
              Part Number
            </th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(2, true)}>
              Model Name
            </th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(3, true)}>
              SBU
            </th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(4, true)}>
              Business
            </th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(5, true)}>
              Market
            </th>
            <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(6, true)}>
              Customer
            </th>
             <th rowSpan="2" className="border border-gray-300 px-2 py-1 text-center" style={getStickyStyle(6, true)}>
              Criteria
            </th>
            {monthHeaders.map((monthName, idx) => (
              <th key={idx} colSpan={3 + childHeaders.length} className="border border-gray-300 px-2 py-1 text-center" style={{ fontSize: '13px' }}>
                {monthName}
              </th>
            ))}
          </tr>
          <tr className="bg-blue-100">
            {monthHeaders.map((monthName, mIdx) => (
              <React.Fragment key={mIdx}>
                <th className="border border-gray-300 px-2 py-1 text-center" style={{ minWidth: '80px' }}>Unit Price</th>
                <th className="border border-gray-300 px-2 py-1 text-center" style={{ minWidth: '80px' }}>Qty</th>
                <th className="border border-gray-300 px-2 py-1 text-center" style={{ minWidth: '80px' }}>Amt</th>
                {childHeaders.map((header, hIdx) => (
                  <th key={hIdx} className="border border-gray-300 px-2 py-1 text-center bg-blue-50" style={{ minWidth: '100px' }}>
                    {header.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </th>
                ))}
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {Object.values(groupedRecords).map((group, rowIdx) => (
            <tr key={rowIdx} className="hover:bg-gray-50">
              <td className="border border-gray-300 px-2 py-1" style={getStickyStyle(0)}>
                {group.customer_name}
              </td>
              <td className="border border-gray-300 px-2 py-1" style={getStickyStyle(1)}>
                {group.part_number}
              </td>
              <td className="border border-gray-300 px-2 py-1" style={getStickyStyle(2)}>
                {group.model_name}
              </td>
              <td className="border text-center border-gray-300 px-2 py-1" style={getStickyStyle(3)}>
                {group.sbu}
              </td>
              <td className="border text-center border-gray-300 px-2 py-1" style={getStickyStyle(4)}>
                {group.business}
              </td>
              <td className="border text-center border-gray-300 px-2 py-1" style={getStickyStyle(5)}>
                {group.market}
              </td>
              <td className="border text-center border-gray-300 px-2 py-1" style={getStickyStyle(6)}>
                {group.is_customer}
              </td>
               <td className="border text-center border-gray-300 px-2 py-1" style={getStickyStyle(7)}>
                {group.criteria}
              </td>
              {monthHeaders.map((monthName, mIdx) => {
                const monthData = group.months[monthName] || {};
                return (
                  <React.Fragment key={mIdx}>
                    {renderEditableCell(group.customer_name, group.part_number, monthName, 'unit_price', monthData.unit_price || 0)}
                    {renderEditableCell(group.customer_name, group.part_number, monthName, 'qty', monthData.qty || 0)}
                    {renderEditableCell(group.customer_name, group.part_number, monthName, 'amt', monthData.amt || 0)}
                    {/* {childHeaders.map((header, hIdx) => (
                      <td key={hIdx} className="border border-gray-300 px-2 py-1 text-right">
                        {monthData[header] || 0}
                      </td>
                    ))} */}
                    {childHeaders.map((header, hIdx) => (
                    <React.Fragment key={hIdx}>
                      {renderEditableCell(group.customer_name, group.part_number, monthName, header, monthData[header] || 0, 'bg-blue-50')}
                    </React.Fragment>
                  ))}
                                    </React.Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};  const handleBudgetSaveAll = async () => {
  
    if (Object.keys(budgetPendingChanges).length === 0) {
      toast.info("No changes to save");
      return;
    }

    try {
      // Group changes by record
      const recordUpdates = {};
      
      Object.values(budgetPendingChanges).forEach(change => {
        const recordKey = `${change.customerName}_${change.partNumber}_${change.month}`;
        if (!recordUpdates[recordKey]) {
          recordUpdates[recordKey] = {
            customerName: change.customerName,
            partNumber: change.partNumber,
            month: change.month,
            fields: {}
          };
        }
        recordUpdates[recordKey].fields[change.field] = change.value;
      });

      // Save each record using budget/update API
      for (const update of Object.values(recordUpdates)) {
        const payload = {
          customer_name: update.customerName,
          part_number: update.partNumber,
          month: update.month,
          ...update.fields
        };

        await service.updateSalesBudget(payload);
      }

      toast.success("All budget changes saved successfully!");
      setBudgetHasUnsavedChanges(false);
      setBudgetPendingChanges({});
      fetchBudgetData(); // Refresh data
    } catch (error) {
      console.error("Error saving budget changes:", error);
      toast.error(error.response?.data?.message || "Error saving budget changes");
    }
  };

  const handleBudgetCancelAll = () => {
    setBudgetPendingChanges({});
    setBudgetHasUnsavedChanges(false);
    fetchBudgetData(); // Reload original data
  };

  // Budget tab editing functions
  const startBudgetEdit = (customerName, partNumber, month, field, currentValue) => {
    const cellData = { customerName, partNumber, month, field };
    let valueToEdit = '';
    if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
      const numVal = typeof currentValue === 'string' ? parseFloat(currentValue) : currentValue;
      valueToEdit = isNaN(numVal) ? '' : String(numVal);
    }
    setBudgetEditingCell(cellData);
    setBudgetEditValue(valueToEdit);
  };

  const saveBudgetEdit = () => {
    if (!budgetEditingCell) return;
    
    const numericValue = parseFloat(budgetEditValue);
    if (isNaN(numericValue)) {
      setBudgetEditingCell(null);
      setBudgetEditValue('');
      return;
    }

    const { customerName, partNumber, month, field } = budgetEditingCell;
    const cellKey = `${customerName}_${partNumber}_${month}_${field}`;

    setBudgetPendingChanges((prev) => ({
      ...prev,
      [cellKey]: {
        customerName,
        partNumber,
        month,
        field,
        value: numericValue
      }
    }));

    setBudgetData((prevData) => {
      if (!prevData || !prevData.data || !Array.isArray(prevData.data)) {
        return prevData;
      }

      const newData = prevData.data.map((record) => {
        if (record.customer_name === customerName && record.part_number === partNumber) {
          const updatedMonths = { ...record.months };
          if (updatedMonths[month]) {
            updatedMonths[month] = {
              ...updatedMonths[month],
              [field]: numericValue
            };
          }
          return { ...record, months: updatedMonths };
        }
        return record;
      });

      return { ...prevData, data: newData };
    });

    setBudgetHasUnsavedChanges(true);
    setBudgetEditingCell(null);
    setBudgetEditValue('');
  };

  // Add export functions
  const handleExportForecast = async () => {
    try {
      if (!data || !data.records || data.records.length === 0) {
        toast.error('No data to export');
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Sales Forecast');

      // Extract month headers from data
      const monthHeaders = data.months || [];
      
      // Extract child headers from first record
      let childHeaders = [];
      if (data.records.length > 0) {
        const firstRecord = data.records[0];
        if (firstRecord.months && monthHeaders.length > 0) {
          const firstMonth = monthHeaders[0];
          if (firstRecord.months[firstMonth]) {
            childHeaders = Object.keys(firstRecord.months[firstMonth]).filter(
              key => key !== 'unit_price' && key !== 'qty' && key !== 'amt' && key !== 'delta'
            );
          }
        }
      }

      // Fixed columns count
      const fixedColumns = ['Customer Name', 'Part Number', 'Model Name', 'SBU', 'Business', 'Market', 'Is Customer', 'Criteria'];
      const columnsPerMonth = 3 + childHeaders.length; // Unit Price, Qty, Amt + dynamic columns

      // Create first header row (Month names merged)
      const headerRow1 = [...fixedColumns];
      monthHeaders.forEach(month => {
        headerRow1.push(month);
        // Add empty cells for merging
        for (let i = 1; i < columnsPerMonth; i++) {
          headerRow1.push('');
        }
      });
      worksheet.addRow(headerRow1);

      // Create second header row (Column names)
      const headerRow2 = [...fixedColumns];
      monthHeaders.forEach(() => {
        headerRow2.push('Unit Price', 'Qty', 'Amt');
        childHeaders.forEach(child => {
          headerRow2.push(child.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
        });
      });
      worksheet.addRow(headerRow2);

      // Merge cells for month headers
      let currentCol = fixedColumns.length + 1; // Start after fixed columns (Excel is 1-indexed)
      monthHeaders.forEach(() => {
        const startCol = currentCol;
        const endCol = currentCol + columnsPerMonth - 1;
        worksheet.mergeCells(1, startCol, 1, endCol);
        currentCol = endCol + 1;
      });

      // Style the header rows
      worksheet.getRow(1).font = { bold: true, size: 11 };
      worksheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFEB3B' } // Yellow background
      };
      worksheet.getRow(1).alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.getRow(2).font = { bold: true, size: 10 };
      worksheet.getRow(2).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' } // Light gray background
      };
      worksheet.getRow(2).alignment = { horizontal: 'center', vertical: 'middle' };

      // Add data rows
      data.records.forEach(group => {
        const row = [
          group.customer_name,
          group.part_number,
          group.model_name,
          group.sbu,
          group.business,
          group.market,
          group.is_customer,
          group.criteria
        ];

        monthHeaders.forEach(monthName => {
          const monthData = group.months[monthName] || {};
          row.push(monthData.unit_price || 0);
          row.push(monthData.qty || 0);
          row.push(monthData.amt || 0);
          childHeaders.forEach(header => {
            row.push(monthData[header] || 0);
          });
        });

        worksheet.addRow(row);
      });

      // Auto-fit columns
      worksheet.columns.forEach((column, index) => {
        if (index < fixedColumns.length) {
          column.width = 20; // Fixed columns wider
        } else {
          column.width = 12; // Data columns
        }
      });

      // Add borders to all cells
      worksheet.eachRow((row, rowNumber) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        });
      });

      // Generate file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sales_forecast_${selectedYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Forecast exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export forecast');
    }
  };

  const handleExportBudget = async () => {
    try {
      if (!budgetData || !budgetData.data || budgetData.data.length === 0) {
        toast.error('No data to export');
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Sales Budget');

      // Extract month headers from budgetData
      const monthHeaders = budgetData.months || [];
      
      // Extract child headers from first record
      let childHeaders = [];
      if (budgetData.data.length > 0) {
        const firstRecord = budgetData.data[0];
        if (firstRecord.months && monthHeaders.length > 0) {
          const firstMonth = monthHeaders[0];
          if (firstRecord.months[firstMonth]) {
            childHeaders = Object.keys(firstRecord.months[firstMonth]).filter(
              key => key !== 'unit_price' && key !== 'qty' && key !== 'amt'
            );
          }
        }
      }

      // Fixed columns count
      const fixedColumns = ['Customer Name', 'Part Number', 'Model Name', 'SBU', 'Business', 'Market', 'Is Customer', 'Criteria'];
      const columnsPerMonth = 3 + childHeaders.length; // Unit Price, Qty, Amt + dynamic columns

      // Create first header row (Month names merged)
      const headerRow1 = [...fixedColumns];
      monthHeaders.forEach(month => {
        headerRow1.push(month);
        // Add empty cells for merging
        for (let i = 1; i < columnsPerMonth; i++) {
          headerRow1.push('');
        }
      });
      worksheet.addRow(headerRow1);

      // Create second header row (Column names)
      const headerRow2 = [...fixedColumns];
      monthHeaders.forEach(() => {
        headerRow2.push('Unit Price', 'Qty', 'Amt');
        childHeaders.forEach(child => {
          headerRow2.push(child.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
        });
      });
      worksheet.addRow(headerRow2);

      // Merge cells for month headers
      let currentCol = fixedColumns.length + 1; // Start after fixed columns (Excel is 1-indexed)
      monthHeaders.forEach(() => {
        const startCol = currentCol;
        const endCol = currentCol + columnsPerMonth - 1;
        worksheet.mergeCells(1, startCol, 1, endCol);
        currentCol = endCol + 1;
      });

      // Style the header rows
      worksheet.getRow(1).font = { bold: true, size: 11 };
      worksheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFEB3B' } // Yellow background
      };
      worksheet.getRow(1).alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.getRow(2).font = { bold: true, size: 10 };
      worksheet.getRow(2).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' } // Light gray background
      };
      worksheet.getRow(2).alignment = { horizontal: 'center', vertical: 'middle' };

      // Add data rows
      budgetData.data.forEach(group => {
        const row = [
          group.customer_name,
          group.part_number,
          group.model_name,
          group.sbu,
          group.business,
          group.market,
          group.is_customer,
          group.criteria
        ];

        monthHeaders.forEach(monthName => {
          const monthData = group.months[monthName] || {};
          row.push(monthData.unit_price || 0);
          row.push(monthData.qty || 0);
          row.push(monthData.amt || 0);
          childHeaders.forEach(header => {
            row.push(monthData[header] || 0);
          });
        });

        worksheet.addRow(row);
      });

      // Auto-fit columns
      worksheet.columns.forEach((column, index) => {
        if (index < fixedColumns.length) {
          column.width = 20; // Fixed columns wider
        } else {
          column.width = 12; // Data columns
        }
      });

      // Add borders to all cells
      worksheet.eachRow((row, rowNumber) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        });
      });

      // Generate file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sales_budget_${budgetYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Budget exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export budget');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#1D1D1D]">Sales Total</h2>
        <button 
          className="bg-[#287DD1] text-white px-6 py-3 rounded-md flex items-center gap-x-2 cursor-pointer"
          onClick={() => navigate('/upload-sales-total')}
        >
          <FaUpload className="text-white" />
          <span className="text-white">Import</span>
        </button>
      </div>

      <Tabs defaultActiveKey="1">
        <TabPane tab="Forecast" key="1">
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center gap-6 mb-4">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Customer:</label>
                <select
                  value={selectedCustomer || ''}
                  onChange={(e) => setSelectedCustomer(Number(e.target.value))}
                  className="border border-gray-300 rounded px-3 py-2"
                  style={{ width: '200px' }}
                >
                  {customers.map(customer => (
                    <option key={customer.customer_id} value={customer.customer_id}>
                      {customer.customer_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Year:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="border border-gray-300 rounded px-3 py-2"
                  style={{ width: '120px' }}
                >
                  {availableForecastYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Quarter:</label>
                <Select
                  mode="multiple"
                  placeholder="Select Quarter(s)"
                  value={selectedQuarters}
                  onChange={(values) => setSelectedQuarters(values)}
                  style={{ minWidth: 280 }}
                  size="large"
                  options={[
                    { label: 'Q1 (Apr-Jun)', value: 'Q1' },
                    { label: 'Q2 (Jul-Sep)', value: 'Q2' },
                    { label: 'Q3 (Oct-Dec)', value: 'Q3' },
                    { label: 'Q4 (Jan-Mar)', value: 'Q4' },
                  ]}
                />
              </div>

              <div>
                <button
                  onClick={fetchSalesTotalData}
                  disabled={loading}
                  className="bg-[#3D3D3D] text-white px-6 py-2 rounded-md hover:bg-[#2D2D2D] disabled:bg-gray-400 cursor-pointer"
                  style={{ height: '40px' }}
                >
                  <span className="text-white">{loading ? 'Loading...' : 'Search'}</span>
                </button>
              </div>

              {/* ✅ Forecast Save Buttons */}
              {hasUnsavedChanges && (
                <>
                  <button 
                    onClick={handleSaveAll}
                    className="bg-[#107C41] text-white px-6 py-2 rounded-md hover:bg-[#0e6b37] cursor-pointer"
                    style={{ height: '40px' }}
                  >
                    <span className="text-white">Save Changes</span>
                  </button>
                  <button 
                    onClick={handleCancelAll}
                    className="bg-[#287DD1] text-white px-6 py-2 rounded-md hover:bg-[#2070b8] cursor-pointer"
                    style={{ height: '40px' }}
                  >
                    <span className="text-white">Cancel</span>
                  </button>
                </>
              )}

              {/* Add Export button */}
              <button
                onClick={handleExportForecast}
                className="bg-green-600 text-white px-4 py-2 rounded-md flex items-center gap-2 hover:bg-green-700"
              >
                <FaFileExcel />
                Export
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-10">
              <p className="text-gray-600">Loading...</p>
            </div>
          ) : data && data.records && data.records.length > 0 ? (
            <>
              {renderDataTable(data)}
              
              {forecastTotal > 0 && (
                <div className="mt-6 flex justify-center">
                  <Pagination
                    current={forecastCurrentPage}
                    pageSize={forecastRecordsPerPage}
                    total={forecastTotal}
                    onChange={(page) => {
                      console.log('📄 Page changed to:', page);
                      setForecastCurrentPage(page);
                    }}
                    showSizeChanger
                    onShowSizeChange={(current, size) => {
                      console.log('📏 Page size changed to:', size);
                      setForecastCurrentPage(1);
                      setForecastRecordsPerPage(size);
                    }}
                    pageSizeOptions={['100', '200', '500']}
                    showTotal={(total) => `Total ${total} records`}
                    className="!mt-4"
                  />
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No forecast data available. Please select year and quarter(s) and click Search.
            </div>
          )}
        </TabPane>

        <TabPane tab="Budget" key="2">
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center gap-6 mb-4">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Customer:</label>
                <select
                  value={selectedBudgetCustomer || ''}
                  onChange={(e) => setSelectedBudgetCustomer(Number(e.target.value))}
                  className="border border-gray-300 rounded px-3 py-2"
                  style={{ width: '200px' }}
                >
                  {budgetCustomers.map(customer => (
                    <option key={customer.customer_id} value={customer.customer_id}>
                      {customer.customer_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Year:</label>
                <select
                  value={budgetYear}
                  onChange={(e) => setBudgetYear(Number(e.target.value))}
                  className="border border-gray-300 rounded px-3 py-2"
                  style={{ width: '120px' }}
                >
                  {availableBudgetYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Quarter:</label>
                <Select
                  mode="multiple"
                  placeholder="Select Quarter(s)"
                  value={budgetQuarters}
                  onChange={(values) => setBudgetQuarters(values)}
                  style={{ minWidth: 280 }}
                  size="large"
                  options={[
                    { label: 'Q1 (Apr-Jun)', value: 'Q1' },
                    { label: 'Q2 (Jul-Sep)', value: 'Q2' },
                    { label: 'Q3 (Oct-Dec)', value: 'Q3' },
                    { label: 'Q4 (Jan-Mar)', value: 'Q4' },
                  ]}
                />
              </div>

              <div>
                <button
                  onClick={fetchBudgetData}
                  disabled={budgetLoading}
                  className="bg-[#3D3D3D] text-white px-6 py-2 rounded-md hover:bg-[#2D2D2D] disabled:bg-gray-400 cursor-pointer"
                  style={{ height: '40px' }}
                >
                  <span className="text-white">{budgetLoading ? 'Loading...' : 'Search'}</span>
                </button>
              </div>
              
              {/* ✅ ADD SAVE BUTTONS HERE */}
              {budgetHasUnsavedChanges && (
                <>
                  <button 
                    onClick={handleBudgetSaveAll}
                    className="bg-[#107C41] text-white px-6 py-2 rounded-md hover:bg-[#0e6b37] cursor-pointer"
                    style={{ height: '40px' }}
                  >
                    <span className="text-white">Save Changes</span>
                  </button>
                  <button 
                    onClick={handleBudgetCancelAll}
                    className="bg-[#287DD1] text-white px-6 py-2 rounded-md hover:bg-[#2070b8] cursor-pointer"
                    style={{ height: '40px' }}
                  >
                    <span className="text-white">Cancel</span>
                  </button>
                </>
              )}

              {/* Add Export button */}
              <button
                onClick={handleExportBudget}
                className="bg-green-600 text-white px-4 py-2 rounded-md flex items-center gap-2 hover:bg-green-700"
              >
                <FaFileExcel />
                Export
              </button>
            </div>
          </div>

          {budgetLoading ? (
            <div className="flex justify-center items-center py-10">
              <p className="text-gray-600">Loading...</p>
            </div>
          ) : budgetData && budgetData.data && budgetData.data.length > 0 ? (
            <>
              {renderBudgetTable(budgetData)}
              
              {budgetTotal > budgetRecordsPerPage && (
                <div className="mt-6 flex justify-center">
                  <Pagination
                    current={budgetCurrentPage}
                    pageSize={budgetRecordsPerPage}
                    total={budgetTotal}
                    onChange={(page) => setBudgetCurrentPage(page)}
                    showSizeChanger
                    onShowSizeChange={(current, size) => {
                      setBudgetCurrentPage(1);
                      setBudgetRecordsPerPage(size);
                    }}
                    pageSizeOptions={['100', '200', '300']}
                    showTotal={(total) => `Total ${total} records`}
                    className="!mt-4"
                  />
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No budget data available. Please select year and quarter(s) and click Search.
            </div>
          )}
        </TabPane>
      </Tabs>
    </div>
  );
};

export default SalesTotal;

const STICKY_WIDTHS = [100, 90, 110, 70, 70, 70, 70];

const getStickyStyle = (index, isHeader = false) => {
  if (index >= 7) return {};

  const left = STICKY_WIDTHS.slice(0, index).reduce((a, b) => a + b, 0);

  return {
    position: "sticky",
    left: `${left}px`,
    zIndex: isHeader ? 50 : 30,
    background: isHeader ? "#fef08a" : "#ffffff",
    boxShadow: index === 6 ? "2px 0 6px rgba(0,0,0,0.08)" : "none",
    minWidth: `${STICKY_WIDTHS[index]}px`,
    width: `${STICKY_WIDTHS[index]}px`
  };
};






